import { useState } from 'react'
import { RowWindow } from '@/components/RowWindow'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChartColumn, Download } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { formatLbp, formatUsd } from '@/lib/money'
import {
  downloadFile,
  type InventoryReport,
  type ProfitAndLoss,
  type SalesReport,
  type VatReport,
  type WasteVoidReport,
} from '@/lib/reports'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Tabs } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { ReportBuilder } from '@/pages/reports/ReportBuilder'
import { PayablesPanel, ReceivablesPanel } from '@/pages/reports/OwedPanels'
import { t } from '@/i18n'

type TabValue =
  | 'sales'
  | 'pnl'
  | 'vat'
  | 'inventory'
  | 'waste'
  | 'payables'
  | 'receivables'
  | 'builder'

function monthStart(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function ReportsPage() {
  const canView = useSession((state) => state.can)(Permission.ViewReports)

  const [tab, setTab] = useState<TabValue>('sales')
  const [from, setFrom] = useState(monthStart)
  const [to, setTo] = useState(today)

  if (!canView) {
    return (
      <div className="pb-page px-4 py-6">
        <EmptyState
          icon={ChartColumn}
          title={t('No access to reports')}
          description={t('Ask an administrator for the reports permission.')}
        />
      </div>
    )
  }

  const period = `from=${from}&to=${to}`

  return (
    <div className="pb-page mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('Reports')}</h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          {t('Days are cut on your own clock, so a daily total matches the drawer you counted.')}
        </p>
      </header>

      <div className="mb-4">
        <Tabs
          tabs={[
            { value: 'sales' as const, label: t('Sales') },
            { value: 'pnl' as const, label: t('Profit & loss') },
            { value: 'vat' as const, label: 'VAT' },
            { value: 'inventory' as const, label: t('Inventory') },
            { value: 'waste' as const, label: t('Waste & voids') },
            { value: 'payables' as const, label: t('You owe') },
            { value: 'receivables' as const, label: t('Owed to you') },
            { value: 'builder' as const, label: t('Build your own') },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      {/* Inventory is a snapshot and the builder carries its own dates. */}
      {tab !== 'inventory' &&
        tab !== 'builder' &&
        tab !== 'payables' &&
        tab !== 'receivables' && (
        <div className="mb-5 grid gap-3 sm:grid-cols-2">
          <Input label={t('From')} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label={t('To')} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      )}

      {tab === 'sales' && <SalesPanel period={period} />}
      {tab === 'pnl' && <ProfitPanel period={period} />}
      {tab === 'vat' && <VatPanel period={period} />}
      {tab === 'inventory' && <InventoryPanel />}
      {tab === 'waste' && <WastePanel period={period} />}
      {tab === 'payables' && <PayablesPanel />}
      {tab === 'receivables' && <ReceivablesPanel />}
      {tab === 'builder' && <ReportBuilder defaultFrom={from} defaultTo={to} />}
    </div>
  )
}

/* ------------------------------------------------------------------- pieces */

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'good' | 'bad'
}) {
  return (
    <div>
      <p className="text-sm text-[var(--text-secondary)]">{label}</p>
      <p
        className={`tabular mt-0.5 text-xl font-semibold ${
          tone === 'good' ? 'text-success-500' : tone === 'bad' ? 'text-danger-500' : ''
        }`}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-[var(--text-secondary)]">{hint}</p>}
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="surface-raised mb-4 rounded-[var(--radius-card)] border p-4">{children}</section>
  )
}

/**
 * Downloads whichever report is on screen.
 *
 * Rendered by the server from the same report object the screen is showing, so the file and the
 * page cannot disagree — nobody should have to wonder which of the two is right.
 */
function ExportButton({ path, query }: { path: string; query: string }) {
  const [error, setError] = useState<string | null>(null)

  const download = useMutation({
    mutationFn: () => downloadFile(`/api/reports/${path}/export?${query}`, `${path}.csv`),
    onSuccess: () => setError(null),
    onError: () => setError('Could not export that.'),
  })

  return (
    <div className="mb-4 flex items-center gap-3">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        <Download className="h-4 w-4" />
        {download.isPending ? 'Exporting…' : 'Export CSV'}
      </Button>
      {error && (
        <p role="alert" className="text-sm text-danger-500">
          {error}
        </p>
      )}
    </div>
  )
}

function Line({
  label,
  value,
  strong,
  negative,
}: {
  label: string
  value: string
  strong?: boolean
  negative?: boolean
}) {
  return (
    <div
      className={`flex justify-between py-1 ${
        strong ? 'border-t border-[var(--border-subtle)] pt-2 font-semibold' : ''
      }`}
    >
      <dt className={negative ? 'text-[var(--text-secondary)]' : ''}>{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  )
}

function Breakdown({
  title,
  rows,
}: {
  title: string
  rows: { label: string; netUsd: number; grossProfitUsd: number }[]
}) {
  if (rows.length === 0) {
    return null
  }

  return (
    <Card>
      <p className="mb-2 text-sm font-medium">{title}</p>
      <ul className="flex flex-col gap-1 text-sm">
        {rows.slice(0, 10).map((row) => (
          <li key={row.label} className="flex justify-between gap-3">
            <span className="min-w-0 truncate">{row.label}</span>
            <span className="tabular shrink-0">
              {formatUsd(row.netUsd)}
              <span className="ml-2 text-xs text-[var(--text-secondary)]">
                {formatUsd(row.grossProfitUsd)} profit
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/* -------------------------------------------------------------------- sales */

/**
 * Says when part of a period was answered from the daily summary rather than its own rows.
 *
 * Without it an empty per-cashier table reads as "nobody sold anything that year", which is a
 * conclusion somebody would act on. The totals are exact either way; it is the breakdowns that are
 * missing, and the difference has to be visible.
 */
function ArchivedNotice({
  detailArchived,
  archivedThrough,
}: {
  detailArchived: boolean
  archivedThrough: string | null
}) {
  if (!detailArchived || !archivedThrough) {
    return null
  }

  return (
    <div className="rounded-[var(--radius-card)] border border-warn-500/40 bg-warn-500/10 px-4 py-3 text-sm">
      Detail before{' '}
      <span className="font-medium">{new Date(archivedThrough).toLocaleDateString()}</span> has been
      archived. Totals for that period come from the daily summary and are exact; per-cashier and
      part-month product breakdowns are not available for it.
    </div>
  )
}

function SalesPanel({ period }: { period: string }) {
  const report = useQuery({
    queryKey: ['report-sales', period],
    queryFn: () => apiClient.get<SalesReport>(`/api/reports/sales?${period}`),
  })

  if (!report.data) {
    return <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
  }

  const data = report.data

  return (
    <>
      <ExportButton path="sales" query={period} />

      <ArchivedNotice detailArchived={data.detailArchived} archivedThrough={data.archivedThrough} />

      <Card>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure label={t('Net sales')} value={formatUsd(data.netUsd)} hint={`${data.saleCount} sales`} />
          <Figure label={t('VAT')} value={formatUsd(data.vatUsd)} />
          <Figure
            label={t('Refunds')}
            value={formatUsd(data.refundsUsd)}
            hint={`${data.refundCount} given back`}
          />
          <Figure
            label={t('Gross profit')}
            value={formatUsd(data.grossProfitUsd)}
            tone={data.grossProfitUsd >= 0 ? 'good' : 'bad'}
            hint={t('after refunds')}
          />
        </div>
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          Average sale {formatUsd(data.averageSaleUsd)} · {data.voidCount} voided · days cut on{' '}
          {data.timeZone}
        </p>
      </Card>

      {data.days.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('By day')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-subtle)] text-left">
                  <th className="py-1.5 pr-3 font-medium">{t('Date')}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t('Sales')}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t('Net')}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t('Refunds')}</th>
                  <th className="py-1.5 text-right font-medium">{t('Profit')}</th>
                </tr>
              </thead>
              <tbody>
                <RowWindow rows={data.days} colSpan={5}>{(day) => (
                  <tr key={day.date} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="py-1.5 pr-3">{day.date}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{day.saleCount}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{formatUsd(day.netUsd)}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{formatUsd(day.refundsUsd)}</td>
                    <td className="tabular py-1.5 text-right">{formatUsd(day.grossProfitUsd)}</td>
                  </tr>
                )}</RowWindow>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Breakdown title={t('By category')} rows={data.byCategory} />
      <Breakdown title={t('By cashier')} rows={data.byCashier} />
      <Breakdown title={t('By branch')} rows={data.byBranch} />

      {data.byPaymentMethod.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('How they paid')}</p>
          <p className="mb-2 text-xs text-[var(--text-secondary)]">
            {t('Cash lines are net of change given, so this is what stayed in the drawer.')}
          </p>
          <ul className="flex flex-col gap-1 text-sm">
            {data.byPaymentMethod.map((row) => (
              <li key={row.label} className="flex justify-between">
                <span>{row.label}</span>
                <span className="tabular">{formatUsd(row.netUsd)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.topProducts.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('Best sellers')}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {data.topProducts.map((product) => (
              <li key={product.productId} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">{product.name}</span>
                <span className="tabular shrink-0">
                  {product.quantity.toLocaleString(undefined, { maximumFractionDigits: 3 })} ·{' '}
                  {formatUsd(product.netUsd)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  )
}

/* ------------------------------------------------------------ profit & loss */

function ProfitPanel({ period }: { period: string }) {
  const report = useQuery({
    queryKey: ['report-pnl', period],
    queryFn: () => apiClient.get<ProfitAndLoss>(`/api/reports/profit-and-loss?${period}`),
  })

  if (!report.data) {
    return <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
  }

  const data = report.data

  return (
    <>
      <ExportButton path="profit-and-loss" query={period} />

      <ArchivedNotice detailArchived={data.detailArchived} archivedThrough={data.archivedThrough} />

      <Card>
        <div className="grid grid-cols-2 gap-4">
          <Figure
            label={t('Net profit')}
            value={formatUsd(data.netProfitUsd)}
            tone={data.netProfitUsd >= 0 ? 'good' : 'bad'}
            hint={`${data.netMarginPercent}% margin`}
          />
          <Figure
            label={t('Gross profit')}
            value={formatUsd(data.grossProfitUsd)}
            hint={`${data.grossMarginPercent}% margin`}
          />
        </div>
      </Card>

      <Card>
        <dl className="text-sm">
          <Line label={t('Gross sales')} value={formatUsd(data.grossSalesUsd)} />
          <Line label={t('Discounts')} value={`−${formatUsd(data.discountsUsd)}`} negative />
          <Line label={t('Refunds')} value={`−${formatUsd(data.refundsUsd)}`} negative />
          <Line label={t('Net revenue')} value={formatUsd(data.netRevenueUsd)} strong />
          <Line label={t('Cost of goods sold')} value={`−${formatUsd(data.costOfGoodsUsd)}`} negative />
          <Line label={t('Gross profit')} value={formatUsd(data.grossProfitUsd)} strong />
          <Line label={t('Waste')} value={`−${formatUsd(data.wasteUsd)}`} negative />
          <Line label={t('Operating expenses')} value={`−${formatUsd(data.operatingExpensesUsd)}`} negative />
          {data.nonOperatingExpensesUsd > 0 && (
            <Line
              label={t('Non-operating')}
              value={`−${formatUsd(data.nonOperatingExpensesUsd)}`}
              negative
            />
          )}
          <Line label={t('Net profit')} value={formatUsd(data.netProfitUsd)} strong />
        </dl>
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          Revenue excludes VAT, which was never the shop's money. Stock bought is not an expense
          until it sells, so it appears in cost of goods and nowhere else.
        </p>
      </Card>

      {/* Below the profit line, deliberately. What the owner took is a share of what was earned,
          not a cost of earning it, so it never moves the figure above. */}
      {data.ownerDrawingsUsd > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('What you took out')}</p>
          <dl className="text-sm">
            <Line label={t('Net profit for the period')} value={formatUsd(data.netProfitUsd)} />
            <Line label={t('Taken from the safe')} value={`−${formatUsd(data.ownerDrawingsUsd)}`} negative />
            <Line label={t('Left in the business')} value={formatUsd(data.retainedUsd)} strong />
          </dl>
          {data.ownerDrawingsLbp > 0 && (
            <p className="tabular mt-2 text-xs text-[var(--text-secondary)]">
              Plus {formatLbp(data.ownerDrawingsLbp)} in lira
            </p>
          )}
          <p className="mt-3 text-xs text-[var(--text-secondary)]">
            Taking money you have already earned does not reduce what you earned, so this sits
            below the profit and does not change it.
          </p>
        </Card>
      )}

      {data.expenses.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('Expenses by category')}</p>
          <ul className="flex flex-col gap-1 text-sm">
            <RowWindow rows={data.expenses}>{(expense) => (
              <li key={expense.category} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  {expense.category}
                  {expense.isNonOperating && (
                    <Badge tone="neutral" className="ml-2">
                      not operating
                    </Badge>
                  )}
                </span>
                <span className="tabular shrink-0">{formatUsd(expense.amountUsd)}</span>
              </li>
            )}</RowWindow>
          </ul>
        </Card>
      )}
    </>
  )
}

/* ---------------------------------------------------------------------- vat */

function VatPanel({ period }: { period: string }) {
  const report = useQuery({
    queryKey: ['report-vat', period],
    queryFn: () => apiClient.get<VatReport>(`/api/reports/vat?${period}`),
  })

  if (!report.data) {
    return <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
  }

  const data = report.data

  return (
    <>
      <ExportButton path="vat" query={period} />

      <ArchivedNotice detailArchived={data.detailArchived} archivedThrough={data.archivedThrough} />

      <Card>
        <Figure
          label={data.payableUsd >= 0 ? 'VAT payable' : 'VAT reclaimable'}
          value={formatUsd(Math.abs(data.payableUsd))}
        />
        <dl className="mt-3 text-sm">
          <Line label={t('Output VAT on sales')} value={formatUsd(data.outputVatUsd)} />
          <Line label={t('Less VAT on refunds')} value={`−${formatUsd(data.refundVatUsd)}`} negative />
          <Line label={t('Net output VAT')} value={formatUsd(data.netOutputVatUsd)} strong />
          <Line label={t('Input VAT on purchases')} value={`−${formatUsd(data.inputVatUsd)}`} negative />
          <Line label={data.payableUsd >= 0 ? 'Payable' : 'Reclaimable'} value={formatUsd(Math.abs(data.payableUsd))} strong />
        </dl>
        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          Banded by the rate each sale actually charged, not by today's rate — changing the rate or
          switching VAT off cannot restate a period you have already filed.
        </p>
      </Card>

      {data.bands.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('By rate')}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {data.bands.map((band) => (
              <li key={band.rate} className="flex justify-between">
                <span>{(band.rate * 100).toFixed(0)}% on {formatUsd(band.netUsd)}</span>
                <span className="tabular">{formatUsd(band.vatUsd)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  )
}

/* ---------------------------------------------------------------- inventory */

function InventoryPanel() {
  const report = useQuery({
    queryKey: ['report-inventory'],
    queryFn: () => apiClient.get<InventoryReport>('/api/reports/inventory'),
  })

  if (!report.data) {
    return <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
  }

  const data = report.data

  return (
    <>
      <ExportButton path="inventory" query="" />

      <Card>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure label={t('Value at cost')} value={formatUsd(data.totalCostValueUsd)} />
          <Figure label={t('Value at retail')} value={formatUsd(data.totalRetailValueUsd)} />
          <Figure label={t('Margin if it all sells')} value={formatUsd(data.potentialMarginUsd)} />
          <Figure
            label={t('Needs attention')}
            value={`${data.lowStockCount + data.expiringCount}`}
            hint={`${data.lowStockCount} low · ${data.expiringCount} expiring`}
          />
        </div>
      </Card>

      {data.byCategory.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-medium">{t('By category')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-subtle)] text-left">
                  <th className="py-1.5 pr-3 font-medium">{t('Category')}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t('Items')}</th>
                  <th className="py-1.5 pr-3 text-right font-medium">{t('At cost')}</th>
                  <th className="py-1.5 text-right font-medium">{t('At retail')}</th>
                </tr>
              </thead>
              <tbody>
                <RowWindow rows={data.byCategory} colSpan={4}>{(row) => (
                  <tr key={row.category} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="py-1.5 pr-3">{row.category}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{row.productCount}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{formatUsd(row.costValueUsd)}</td>
                    <td className="tabular py-1.5 text-right">{formatUsd(row.retailValueUsd)}</td>
                  </tr>
                )}</RowWindow>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data.deadStock.length > 0 && (
        <Card>
          <p className="mb-1 text-sm font-medium">{t('Not selling')}</p>
          <p className="mb-2 text-xs text-[var(--text-secondary)]">
            {t('Money already spent, sitting on a shelf.')}
          </p>
          <ul className="flex flex-col gap-1 text-sm">
            <RowWindow rows={data.deadStock}>{(row) => (
              <li key={row.productId} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">{row.name}</span>
                <span className="tabular shrink-0">
                  {formatUsd(row.costValueUsd)}
                  <span className="ml-2 text-xs text-[var(--text-secondary)]">
                    {row.daysSinceSold === null ? 'never sold' : `${row.daysSinceSold}d`}
                  </span>
                </span>
              </li>
            )}</RowWindow>
          </ul>
        </Card>
      )}
    </>
  )
}

/* -------------------------------------------------------------------- waste */

function WastePanel({ period }: { period: string }) {
  const report = useQuery({
    queryKey: ['report-waste', period],
    queryFn: () => apiClient.get<WasteVoidReport>(`/api/reports/waste-and-voids?${period}`),
  })

  if (!report.data) {
    return <p className="text-sm text-[var(--text-secondary)]">{t('Loading…')}</p>
  }

  const data = report.data

  return (
    <>
      <ExportButton path="waste-and-voids" query={period} />

      <Card>
        <div className="grid grid-cols-2 gap-4">
          <Figure
            label={t('Written off')}
            value={formatUsd(data.wasteCostUsd)}
            hint={`${data.wasteCount} write-offs, at cost`}
          />
          <Figure
            label={t('Voided')}
            value={formatUsd(data.voidValueUsd)}
            hint={`${data.voidCount} sales cancelled`}
          />
        </div>
      </Card>

      {[
        { title: t('Waste by reason'), rows: data.wasteByReason, money: (r: { costUsd: number }) => r.costUsd },
        { title: t('Waste by person'), rows: data.wasteByUser, money: (r: { costUsd: number }) => r.costUsd },
        { title: t('Voids by person'), rows: data.voidsByUser, money: (r: { valueUsd: number }) => r.valueUsd },
      ].map((section) =>
        section.rows.length === 0 ? null : (
          <Card key={section.title}>
            <p className="mb-2 text-sm font-medium">{section.title}</p>
            <ul className="flex flex-col gap-1 text-sm">
              {section.rows.map((row) => (
                <li key={row.label} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate">{row.label}</span>
                  <span className="tabular shrink-0">
                    {formatUsd((section.money as (r: typeof row) => number)(row))}
                    <span className="ml-2 text-xs text-[var(--text-secondary)]">×{row.count}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ),
      )}

      <p className="text-xs text-[var(--text-secondary)]">
        Both lists name the person who authorised each one. A pattern under one name is what this
        report is for.
      </p>
    </>
  )
}
